import axios from 'axios';
import * as Sentry from '@sentry/react';
import { useState, useEffect, createContext, ReactNode, useContext } from 'react';
import FullscreenLoader from '../components/FullscreenLoader';
import { ConfigType, ReportType } from '../helpers/types';
import i18n from '../i18n';
import EntitySelector from '../screens/EntitySelector';
import { fetchTemplates, readOidcSub, toFlatApplicationConfig } from '../api/applicationTemplates';

/**
 * Henter maler fra `/rest/applicationtemplate` og bygger en flat liste som
 * erstatter legacy `application_types` på Dashboard. Faller tilbake på legacy
 * listen hvis endpoint ikke er tilgjengelig.
 */
async function fetchTemplatesAndMerge(
    sub: string,
    hostname: string,
    applicationTypes: Record<string, any>[]
): Promise<Record<string, any>[]> {
    const rows = await fetchTemplates(sub, hostname);
    if (!rows || rows.length === 0) return applicationTypes;

    const legacyByType = new Map<number, Record<string, any>>();
    for (const at of applicationTypes) legacyByType.set(Number(at.type), at);

    return rows.map((tpl) => toFlatApplicationConfig(tpl, legacyByType.get(Number(tpl.type))));
}

type ConfigContextType = {
    data: ConfigType | null;
    applications: Record<string, any> | null;
    reportData: ReportType[] | null;
    activeReportStatuses: Array<number>;
    draftId: number | null;
    setDraftId: (value: number | null) => void;
    reportDraftId: number | null;
    setReportDraftId: (value: number | null) => void;
    skipDraftChoice: boolean;
    setSkipDraftChoice: (value: boolean) => void;
    privacy: string | null;
    companyWebsite: string | null;
    cmEnabled: boolean;
    /**
     * Entity-innstillingen `disable_illustrations`. Når den er på skal de
     * innebygde stock-illustrasjonene (intro.svg, soknad-gfx.png, tips.svg)
     * utelates. Bilder stiftelsen selv har lastet opp — tips-ikoner og
     * bakgrunnsmedia i seksjonsmodaler — er bevisst ikke omfattet.
     */
    disableIllustrations: boolean;
};

const ConfigContext = createContext<ConfigContextType | null>(null);

const useConfigContext = () => {
    const context = useContext(ConfigContext);
    if (context === null) {
        throw new Error('useConfigContext must be used within a ConfigProvider');
    }

    return context;
}

const ConfigProvider = ({ children }: { children: ReactNode }) => {
    const [data, setData] = useState<ConfigType | null>(null);
    const [applications, setApplications] = useState<Record<string, any> | null>(null);
    const [reportData, setReportData] = useState<ReportType[] | null>(null);
    const [draftId, setDraftId] = useState<number | null>(null);
    const [reportDraftId, setReportDraftId] = useState<number | null>(null);
    const [skipDraftChoice, setSkipDraftChoice] = useState<boolean>(false);
    const [privacy, setPrivacy] = useState<string | null>(null);
    const [companyWebsite, setCompanyWebsite] = useState<string | null>(null);
    const activeReportStatuses = [0, 9];
    const [noConfig, setNoConfig] = useState(false);
    const [cmEnabled, setCmEnabled] = useState<boolean>(false);
    const [disableIllustrations, setDisableIllustrations] = useState<boolean>(false);
    // Råe `application_types` fra legacy fetchConfig — holdes i state slik at
    // vi kan re-merge mot DB-maler når OIDC `sub` blir tilgjengelig etter
    // BankID-redirect (lyfti:sub-ready-event).
    const [legacyApplicationTypes, setLegacyApplicationTypes] = useState<Record<string, any>[] | null>(null);

    const fetchConfig = async (domain: string, currentSub: string) => {
        try {
            const response = await axios.post(`${import.meta.env.VITE_API_URL}`, {
                action: 'fetchConfig',
                hostname: domain
            });

            const configJson = await response.data.json;
            if(!configJson) {
                setNoConfig(true);
                return;
            }

            setNoConfig(false);
            Sentry.setTag('entity', String(configJson['entity_id']));
            setData({
                id: configJson['entity_id'],
                title: configJson['entity_name'],
                short_name: configJson['entity_name'],
                logo: configJson['logo_application_portal'],
                config_version: configJson['config_version'],
                cookiebot_id: configJson['cookieBot'],
                url_webpage_contact: configJson['url_webpage_contact'],
                max_file_size_mb: Number(configJson['max_file_size'] || 100),
                entity_path: configJson['entity_path']
            });

            // Checking locale
            if (configJson['lang']) {
                i18n.changeLanguage(configJson['lang']);
            }

            // Apply language overrides if any
            if(configJson['lang_overrides']){
                const overrides = configJson['lang_overrides']
                overrides.forEach((override: Record<string, string>) => {
                    Object.keys(override).forEach((key) => {
                        i18n.addResource(configJson['lang'], "translation", key, override[key]);
                    });
                });
            }

            const legacyTypes = configJson['application_types'] ?? [];
            setLegacyApplicationTypes(legacyTypes);

            // Slå sammen entity-config med form-skjema fra /rest/applicationtemplate.
            // Hvis sub fortsatt er tom (typisk ved første mount før BankID-
            // redirect), brukes legacy-listen midlertidig — vi re-merger
            // automatisk når `lyfti:sub-ready`-eventen kommer.
            const merged = await fetchTemplatesAndMerge(
                currentSub,
                domain,
                legacyTypes
            );
            setApplications(merged);

            setReportData(configJson['reports']);
            setPrivacy(configJson['privacy_text']);
            setCompanyWebsite(configJson['url_webpage']);
            setCmEnabled(configJson['cm_enabled'] || false);
            setDisableIllustrations(configJson['disable_illustrations'] || false);
        } catch (error) {
            console.error("FetchConfig ERROR: ", error);
        }
    }

    useEffect(() => {
        // Første runde: hent legacy fetchConfig. `sub` leses synkront fra
        // sessionStorage — vil ofte være tom her, men det fikser
        // `lyfti:sub-ready`-handleren under når OIDC blir klar.
        const saved = localStorage.getItem('lyfti_selected_entity');
        const sub = readOidcSub();
        fetchConfig(saved || window.location.hostname, sub);
    }, []);

    useEffect(() => {
        // Re-hent maler fra /rest/applicationtemplate så snart AuthProvider
        // har fått `sub` fra OIDC. Vi har allerede legacy `application_types`
        // i state, så vi trenger ikke kalle fetchConfig på nytt — bare mergen.
        const handler = async (e: Event) => {
            const detail = (e as CustomEvent<{ sub: string }>).detail;
            const newSub = detail?.sub;
            if (!newSub) return;
            if (!legacyApplicationTypes) return;

            const saved = localStorage.getItem('lyfti_selected_entity');
            const hostname = saved || window.location.hostname;
            const merged = await fetchTemplatesAndMerge(newSub, hostname, legacyApplicationTypes);
            setApplications(merged);
        };
        window.addEventListener('lyfti:sub-ready', handler as EventListener);
        return () => window.removeEventListener('lyfti:sub-ready', handler as EventListener);
    }, [legacyApplicationTypes]);

    if(noConfig) return <EntitySelector />;

    return (
        <ConfigContext.Provider value={{
            data,
            applications,
            reportData,
            activeReportStatuses,
            draftId,
            setDraftId,
            reportDraftId,
            setReportDraftId,
            skipDraftChoice,
            setSkipDraftChoice,
            privacy,
            companyWebsite,
            cmEnabled,
            disableIllustrations
        }}
        >
            {data && applications ? children : <FullscreenLoader />}
        </ConfigContext.Provider>
    )
}

export { useConfigContext, ConfigProvider }